import actionTypes from "../../common/actionTypes";
import { DATABASE_STATUTES } from "../../common/constants";
import { buildCustomerExperienceRequestKey } from "../../common/V2/customerExperienceConstants";

export const customExperienceReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES:
      return {
        ...state,
        errorMarketplace: null,
        statusMarketplace: DATABASE_STATUTES.LOADING,
      };

    case actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES_SUCCESS:
      return {
        ...state,
        marketplaceCustomerExperiences: action.payload.marketplaceCustomerExperiences || [],
        marketplacePagination: action.payload.pagination || state.marketplacePagination,
        statusMarketplace: DATABASE_STATUTES.LOADED,
      };

    case actionTypes.FETCH_MARKETPLACE_CUSTOMER_EXPERIENCES_FAILURE:
      return {
        ...state,
        errorMarketplace: action.error,
        statusMarketplace: DATABASE_STATUTES.LOADED_ERROR,
      };

    case actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES:
      return {
        ...state,
        errorShop: null,
        statusShop: DATABASE_STATUTES.LOADING,
      };

    case actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES_SUCCESS:
      return {
        ...state,
        shopCustomerExperiences: action.payload.customerExperiences || [],
        shopPagination: action.payload.pagination || state.shopPagination,
        statusShop: DATABASE_STATUTES.LOADED,
      };

    case actionTypes.FETCH_SHOP_CUSTOMER_EXPERIENCES_FAILURE:
      return {
        ...state,
        errorShop: action.error,
        statusShop: DATABASE_STATUTES.LOADED_ERROR,
      };

    case actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorMarketplace: null,
      };

    case actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE_SUCCESS: {
      const duplicatedCustomerExperience = action.payload?.customerExperience;

      return {
        ...state,
        // Add the duplicated experience to the shop experiences and seed detail state for the redirect.
        shopCustomerExperiences: duplicatedCustomerExperience
          ? [duplicatedCustomerExperience, ...state.shopCustomerExperiences]
          : state.shopCustomerExperiences,
        customerExperience: duplicatedCustomerExperience || state.customerExperience,
        customerExperienceRequestKey: duplicatedCustomerExperience
          ? buildCustomerExperienceRequestKey("shop", duplicatedCustomerExperience.id)
          : state.customerExperienceRequestKey,
        errorCustomerExperience: null,
        statusCustomerExperience: duplicatedCustomerExperience
          ? DATABASE_STATUTES.LOADED
          : state.statusCustomerExperience,
      };
    }

    case actionTypes.DUPLICATE_MARKETPLACE_CUSTOMER_EXPERIENCE_FAILURE:
      return {
        ...state,
        errorMarketplace: action.error,
      };

    case actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorShop: null,
      };

    case actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS:
      return {
        ...state,
        // Add the duplicated experience to the shop experiences (at the beginning since it's the newest)
        shopCustomerExperiences: [action.payload.customerExperience, ...state.shopCustomerExperiences],
      };

    case actionTypes.DUPLICATE_SHOP_CUSTOMER_EXPERIENCE_FAILURE:
      return {
        ...state,
        errorShop: action.error,
      };

    case actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorShop: null,
      };

    case actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS:
      return {
        ...state,
        shopCustomerExperiences: [action.payload.customerExperience, ...state.shopCustomerExperiences],
      };

    case actionTypes.CREATE_SHOP_CUSTOMER_EXPERIENCE_FAILURE:
      return {
        ...state,
        errorShop: action.error,
      };

    case actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorShop: null,
      };

    case actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE_SUCCESS:
      return {
        ...state,
        // Remove the deleted experience from shop experiences
        shopCustomerExperiences: state.shopCustomerExperiences.filter(
          (experience) => experience.id !== action.payload.customerExperienceId
        ),
      };

    case actionTypes.DELETE_SHOP_CUSTOMER_EXPERIENCE_FAILURE:
      // Don't change statusShop - the list is still valid, only the delete failed
      // The error is handled by the modal component
      return {
        ...state,
        errorShop: action.error,
      };

    case actionTypes.CLEAR_SHOP_CUSTOMER_EXPERIENCES:
      return {
        ...state,
        shopCustomerExperiences: [],
        shopPagination: {},
        statusShop: DATABASE_STATUTES.LOADING,
      };

    case actionTypes.FETCH_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorCustomerExperience: null,
        statusCustomerExperience: DATABASE_STATUTES.LOADING,
      };

    case actionTypes.FETCH_CUSTOMER_EXPERIENCE_SUCCESS:
      return {
        ...state,
        customerExperience: action.payload.customerExperience || null,
        customerExperienceRequestKey: action.meta?.requestKey ?? null,
        statusCustomerExperience: DATABASE_STATUTES.LOADED,
      };

    case actionTypes.FETCH_CUSTOMER_EXPERIENCE_FAILURE:
      return {
        ...state,
        errorCustomerExperience: action.error,
        statusCustomerExperience: DATABASE_STATUTES.LOADED_ERROR,
      };

    case actionTypes.UPDATE_CUSTOMER_EXPERIENCE:
      return {
        ...state,
        errorCustomerExperience: null,
      };

    case actionTypes.UPDATE_CUSTOMER_EXPERIENCE_SUCCESS: {
      const updatedCustomerExperience = action.payload.customerExperience || null;

      return {
        ...state,
        customerExperience: updatedCustomerExperience,
        shopCustomerExperiences: updatedCustomerExperience
          ? state.shopCustomerExperiences.map((experience) =>
              experience.id === updatedCustomerExperience.id ? updatedCustomerExperience : experience
            )
          : state.shopCustomerExperiences,
        statusCustomerExperience: DATABASE_STATUTES.LOADED,
      };
    }

    case actionTypes.UPDATE_CUSTOMER_EXPERIENCE_FAILURE:
      // A failed *save* must not flip the detail page into the "not found" state.
      // errorCustomerExperience / LOADED_ERROR are reserved for load (FETCH) failures;
      // here the CE is still loaded. The save error is surfaced by the settings
      // save-error toast, and the SaveBar stays dirty so the user can correct and retry.
      return state;

    case actionTypes.FETCH_LOCATIONS:
      return {
        ...state,
        errorLocations: null,
        statusLocations: DATABASE_STATUTES.LOADING,
      };

    case actionTypes.FETCH_LOCATIONS_SUCCESS:
      return {
        ...state,
        locations: action.payload || [],
        statusLocations: DATABASE_STATUTES.LOADED,
      };

    case actionTypes.FETCH_LOCATIONS_FAILURE:
      return {
        ...state,
        errorLocations: action.error,
        statusLocations: DATABASE_STATUTES.LOADED_ERROR,
      };

    default:
      return state;
  }
};
